/** URL helpers for projects (today: `leads` table). Phase 1: `/projects` paths with UUID slugs. */

const instancePrefix = (): string => {
  if (typeof window === 'undefined') return '';
  const match = window.location.pathname.match(/^\/i\/[^/]+/);
  return match ? match[0] : '';
};

/** Project list view modes (`LeadsPage`). */
export type ProjectListView = 'pipeline' | 'list' | 'dashboard';

const PROJECT_LIST_VIEW_TO_SLUG: Record<ProjectListView, string> = {
  pipeline: 'pipeline',
  list: 'list',
  dashboard: 'dashboard',
};

const PROJECT_LIST_SLUG_TO_VIEW = Object.fromEntries(
  (Object.entries(PROJECT_LIST_VIEW_TO_SLUG) as [ProjectListView, string][]).map(([view, slug]) => [
    slug,
    view,
  ]),
) as Record<string, ProjectListView>;

export function projectListViewSlug(view: ProjectListView): string {
  return PROJECT_LIST_VIEW_TO_SLUG[view];
}

export function projectListViewFromSlug(slug: string | undefined): ProjectListView | null {
  if (!slug) return null;
  return PROJECT_LIST_SLUG_TO_VIEW[slug] ?? null;
}

/** Project list (`LeadsPage`). Default view: pipeline. */
export function projectsListPath(view: ProjectListView = 'pipeline'): string {
  return `${instancePrefix()}/projects/${projectListViewSlug(view)}`;
}

/** Project detail — canonical slug is internal UUID only. */
export function projectDetailPath(project: { id: number | string }): string {
  return `${instancePrefix()}/projects/${encodeURIComponent(String(project.id))}`;
}

/** Maps a German document-type label to a URL-safe slug and back. */
export const DOCUMENT_TYPE_SLUGS: Record<string, string> = {
  Angebot: 'offer',
  Kostenvoranschlag: 'quote',
  'Auftragsbestätigung': 'order-confirmation',
  Lieferschein: 'delivery-note',
  Rechnung: 'invoice',
  Abschlagsrechnung: 'partial-invoice',
  Schlussrechnung: 'final-invoice',
  Stornorechnung: 'credit-note',
  Gutschrift: 'invoice-correction',
  Arbeitsbericht: 'work-report',
};

/** Reverse map: slug → label. */
export const DOCUMENT_SLUG_TO_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(DOCUMENT_TYPE_SLUGS).map(([label, slug]) => [slug, label]),
);

/** Dokumenttypen für „Neues Dokument erstellen“ (Projekt-Detail, Posteingang, …). */
export const PROJECT_DOCUMENT_TYPE_LABELS = [
  'Angebot',
  'Kostenvoranschlag',
  'Auftragsbestätigung',
  'Lieferschein',
  'Rechnung',
  'Abschlagsrechnung',
  'Schlussrechnung',
  'Stornorechnung',
  'Gutschrift',
  'Arbeitsbericht',
] as const;

export type ProjectDocumentTypeLabel = (typeof PROJECT_DOCUMENT_TYPE_LABELS)[number];

/** Project detail with a selected offering (Angebote tab). */
export function projectOfferingDetailPath(
  project: { id: number | string },
  offering: { id: string },
): string {
  return `${projectDetailPath(project)}/offerings/${encodeURIComponent(offering.id)}`;
}

/** Full editor URL for project + offering + document type label. */
export function projectOfferingDocumentPath(
  project: { id: number | string },
  offering: { id: string },
  docTypeLabel: string,
): string {
  const base = projectOfferingDetailPath(project, offering);
  const slug = DOCUMENT_TYPE_SLUGS[docTypeLabel] ?? docTypeLabel.toLowerCase();
  return `${base}/${slug}`;
}

/** Editor URL when the route already uses a document slug. */
export function projectOfferingDocumentPathFromSlug(
  project: { id: number | string },
  offering: { id: string },
  docTypeSlug: string,
): string {
  return `${projectOfferingDetailPath(project, offering)}/${encodeURIComponent(docTypeSlug)}`;
}

/** Open document editor: `/projects/{uuid}/{docSlug}?offering={offeringUuid}`. */
export function projectDocumentEditorPath(
  project: { id: number | string },
  docTypeSlug: string,
  offeringId: string,
): string {
  const base = projectDocumentPathFromSlug(project, docTypeSlug);
  return `${base}?offering=${encodeURIComponent(offeringId)}`;
}

/** Same as `projectDocumentEditorPath` with a German document label. */
export function projectDocumentEditorPathFromLabel(
  project: { id: number | string },
  docTypeLabel: string,
  offeringId: string,
): string {
  const slug = DOCUMENT_TYPE_SLUGS[docTypeLabel] ?? docTypeLabel.toLowerCase();
  return projectDocumentEditorPath(project, slug, offeringId);
}

/** Full editor URL for a project + document type label (no offering query). */
export function projectDocumentPath(
  project: { id: number | string },
  docTypeLabel: string,
): string {
  const base = projectDetailPath(project);
  const slug = DOCUMENT_TYPE_SLUGS[docTypeLabel] ?? docTypeLabel.toLowerCase();
  return `${base}/${slug}`;
}

/** @deprecated Prefer `projectOfferingDocumentPathFromSlug` when an offering is known. */
export function projectDocumentPathFromSlug(
  project: { id: number | string },
  docTypeSlug: string,
): string {
  return `${projectDetailPath(project)}/${encodeURIComponent(docTypeSlug)}`;
}
